import ReactPixel from 'react-facebook-pixel';

class MetaPixelService {
  constructor() {
    this.isInitialized = false;
    this.pixelId = process.env.REACT_APP_META_PIXEL_ID || '';
  }

  /**
   * Initialize Meta Pixel
   * @param {string} pixelId - Optional pixel ID, defaults to environment variable
   * @param {Object} options - Pixel options
   */
  init(pixelId = null, options = {}) {
    const finalPixelId = pixelId || this.pixelId;
    
    if (!finalPixelId) {
      console.warn('Meta Pixel: No pixel ID provided. Set REACT_APP_META_PIXEL_ID environment variable.');
      return;
    }

    if (this.isInitialized) {
      console.warn('Meta Pixel: Already initialized');
      return;
    }

    try {
      const pixelOptions = {
        autoConfig: true,
        debug: process.env.NODE_ENV === 'development',
        ...options
      };

      ReactPixel.init(finalPixelId, pixelOptions);
      this.isInitialized = true;
      console.log('Meta Pixel initialized successfully with ID:', finalPixelId);
    } catch (error) {
      console.error('Meta Pixel initialization failed:', error);
    }
  }

  /**
   * Track page view
   * @param {string} pageName - Optional page name for tracking
   */
  pageView(pageName = null) {
    if (!this.isInitialized) {
      console.warn('Meta Pixel: Not initialized. Call init() first.');
      return;
    }

    try {
      ReactPixel.pageView();
      console.log('Meta Pixel: Page view tracked', pageName ? `for ${pageName}` : '');
    } catch (error) {
      console.error('Meta Pixel: Page view tracking failed:', error);
    }
  }

  /**
   * Track custom events
   * @param {string} eventName - Event name (e.g., 'Purchase', 'Lead', 'AddToCart')
   * @param {Object} parameters - Event parameters
   */
  track(eventName, parameters = {}) {
    if (!this.isInitialized) {
      console.warn('Meta Pixel: Not initialized. Call init() first.');
      return;
    }

    try {
      ReactPixel.track(eventName, parameters);
      console.log('Meta Pixel: Event tracked:', eventName, parameters);
    } catch (error) {
      console.error('Meta Pixel: Event tracking failed:', error);
    }
  }

  /**
   * Track conversions with value
   * @param {string} eventName - Conversion event name
   * @param {Object} parameters - Conversion parameters including value, currency, etc.
   */
  trackConversion(eventName, parameters = {}) {
    if (!this.isInitialized) {
      console.warn('Meta Pixel: Not initialized. Call init() first.');
      return;
    }

    try {
      const conversionData = {
        value: parameters.value || 0,
        currency: parameters.currency || 'USD',
        ...parameters
      };

      ReactPixel.track(eventName, conversionData);
      console.log('Meta Pixel: Conversion tracked:', eventName, conversionData);
    } catch (error) {
      console.error('Meta Pixel: Conversion tracking failed:', error);
    }
  }

  /**
   * Track lead generation
   * @param {Object} leadData - Lead information
   */
  trackLead(leadData = {}) {
    this.track('Lead', leadData);
  }

  /**
   * Track user registration
   * @param {Object} userData - User registration data
   */
  trackRegistration(userData = {}) {
    this.track('CompleteRegistration', userData);
  }

  /**
   * Track purchase/subscription
   * @param {Object} purchaseData - Purchase information
   */
  trackPurchase(purchaseData = {}) {
    this.trackConversion('Purchase', purchaseData);
  }

  /**
   * Track form submissions
   * @param {string} formType - Type of form submitted
   * @param {Object} formData - Form data
   */
  trackFormSubmission(formType, formData = {}) {
    this.track('SubmitApplication', {
      content_name: formType,
      ...formData
    });
  }

  /**
   * Track website analysis requests
   * @param {Object} analysisData - Analysis request data
   */
  trackWebsiteAnalysis(analysisData = {}) {
    this.track('Search', {
      search_string: analysisData.url || '',
      content_category: 'website_analysis',
      ...analysisData
    });
  }

  /**
   * Track support ticket creation
   * @param {Object} ticketData - Support ticket data
   */
  trackSupportTicket(ticketData = {}) {
    this.track('Contact', {
      content_category: 'support_ticket',
      ...ticketData
    });
  }

  /**
   * Check if pixel is initialized
   * @returns {boolean}
   */
  isReady() {
    return this.isInitialized;
  }

  /**
   * Get current pixel ID
   * @returns {string}
   */
  getPixelId() {
    return this.pixelId;
  }
}

// Export singleton instance
const metaPixelService = new MetaPixelService();
export default metaPixelService;
